import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Banner, Navbar, Footer } from '../components/landing/Shared';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Send, CheckCircle, Building2, Users, Shield, Zap, HeadphonesIcon, Lock } from 'lucide-react';
import { axFetch } from '../lib/utils';

const Enterprise = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    teamSize: '',
    phone: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!contactForm.email.trim() || !contactForm.company.trim()) {
      setSubmitError('Please fill in required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email)) {
      setSubmitError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await axFetch('POST', 'contact-us', {
        name: contactForm.name,
        email: contactForm.email,
        company: contactForm.company,
        message: `Enterprise Inquiry\n\nTeam Size: ${contactForm.teamSize}\nPhone: ${contactForm.phone}\n\nMessage:\n${contactForm.description}`
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send message');
      }

      setSubmitSuccess(true);
      setContactForm({
        name: '',
        email: '',
        company: '',
        teamSize: '',
        phone: '',
        description: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 8000);

    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignOut = () => {
    logout();
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  const handleHeroCTA = () => {
    const element = document.getElementById('contact-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#1d1d1f] antialiased" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <Banner />
      <Navbar
        onRunClick={() => navigate('/download')}
        isAuthenticated={isAuthenticated}
        onSignOut={handleSignOut}
        onDashboard={handleDashboard}
      />

      <main className="pt-32 pb-20 px-6 excel-grid min-h-screen">
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 liquid-glass-badge text-[#0071e3] rounded-[24px] mb-8">
              <Building2 size={36} strokeWidth={1.5} />
            </div>
            <h1 className="text-[48px] md:text-[64px] font-semibold text-[#1d1d1f] mb-6 tracking-[-0.02em] leading-[1.1]" style={{ fontWeight: 600 }}>
              Alt-X for Enterprise
            </h1>
            <p className="text-[19px] md:text-[21px] text-[#6e6e73] max-w-3xl mx-auto leading-[1.5]">
              Custom deployments, dedicated support, and enterprise-grade security for institutional investment teams
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-20">
            {[
              {
                icon: <Users size={24} strokeWidth={1.5} />,
                title: "Team Management",
                desc: "Centralized license management, user provisioning, and team analytics dashboard"
              },
              {
                icon: <Shield size={24} strokeWidth={1.5} />,
                title: "SSO & Compliance",
                desc: "SAML/OAuth integration, SOC2 compliance, and on-premise deployment options"
              },
              {
                icon: <HeadphonesIcon size={24} strokeWidth={1.5} />,
                title: "Dedicated Support",
                desc: "Priority support with dedicated account manager and 24/7 phone support"
              },
              {
                icon: <Lock size={24} strokeWidth={1.5} />,
                title: "Data Sovereignty",
                desc: "Keep your models on your infrastructure with on-premise or private cloud deployment"
              },
              {
                icon: <Zap size={24} strokeWidth={1.5} />,
                title: "Custom Rules Engine",
                desc: "Define your own model validation rules and firm-specific best practices"
              },
              {
                icon: <Building2 size={24} strokeWidth={1.5} />,
                title: "White-Label Option",
                desc: "Custom branding and integration with your existing investment platforms"
              }
            ].map((feature, i) => (
              <div key={i} className="p-8 liquid-glass rounded-[24px] border border-white/20">
                <div className="text-[#0071e3] mb-4">{feature.icon}</div>
                <h3 className="text-[17px] font-semibold text-[#1d1d1f] mb-3 tracking-[-0.01em]">{feature.title}</h3>
                <p className="text-[14px] text-[#6e6e73] leading-[1.5]">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Pricing Section */}
          <div className="bg-gradient-to-br from-blue-50/40 via-blue-100/30 to-purple-50/40 border border-blue-100/30 rounded-[32px] p-12 mb-20">
            <div className="text-center mb-8">
              <h2 className="text-[36px] md:text-[44px] font-semibold text-[#1d1d1f] mb-4 tracking-[-0.02em]" style={{ fontWeight: 600 }}>
                Enterprise Pricing
              </h2>
              <p className="text-[17px] text-[#6e6e73] max-w-2xl mx-auto">
                Custom pricing based on team size and deployment requirements
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50">
                <div className="text-[15px] text-[#6e6e73] mb-2">Starting at</div>
                <div className="text-[32px] font-semibold text-[#1d1d1f] mb-1" style={{ fontWeight: 600 }}>$300</div>
                <div className="text-[13px] text-[#6e6e73]">per month</div>
              </div>
              <div className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50">
                <div className="text-[15px] text-[#6e6e73] mb-2">Team Plans</div>
                <div className="text-[32px] font-semibold text-[#1d1d1f] mb-1" style={{ fontWeight: 600 }}>Custom</div>
                <div className="text-[13px] text-[#6e6e73]">5+ seats</div>
              </div>
              <div className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50">
                <div className="text-[15px] text-[#6e6e73] mb-2">Enterprise</div>
                <div className="text-[32px] font-semibold text-[#1d1d1f] mb-1" style={{ fontWeight: 600 }}>Contact</div>
                <div className="text-[13px] text-[#6e6e73]">50+ seats</div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div id="contact-form" className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-[36px] md:text-[44px] font-semibold text-[#1d1d1f] mb-4 tracking-[-0.02em]" style={{ fontWeight: 600 }}>
                Get in Touch
              </h2>
              <p className="text-[17px] text-[#6e6e73]">
                Fill out the form below and our team will reach out within 24 hours
              </p>
            </div>

            <div className="ultra-glass rounded-[40px] p-10 md:p-12 border-2 border-white/30">
              {submitSuccess && (
                <div className="mb-8 p-5 bg-green-50 border border-green-200 rounded-2xl">
                  <div className="flex items-center mb-2">
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                    <p className="text-green-800 font-semibold text-[15px]">Message sent successfully!</p>
                  </div>
                  <p className="text-green-700 text-[14px] ml-9">
                    Our enterprise team will contact you within 24 hours.
                  </p>
                </div>
              )}

              {submitError && (
                <div className="mb-8 p-5 bg-red-50 border border-red-200 rounded-2xl">
                  <p className="text-red-800 text-[14px]">{submitError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[14px] font-medium text-[#1d1d1f] mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                      className="w-full bg-white border-zinc-300 h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-[14px] font-medium text-[#1d1d1f] mb-2">
                      Work Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleInputChange}
                      placeholder="john@fund.com"
                      required
                      className="w-full bg-white border-zinc-300 h-12"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[14px] font-medium text-[#1d1d1f] mb-2">
                      Company / Fund <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="company"
                      value={contactForm.company}
                      onChange={handleInputChange}
                      placeholder="Your firm"
                      required
                      className="w-full bg-white border-zinc-300 h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-[14px] font-medium text-[#1d1d1f] mb-2">
                      Team Size
                    </label>
                    <Input
                      type="text"
                      name="teamSize"
                      value={contactForm.teamSize}
                      onChange={handleInputChange}
                      placeholder="e.g., 5-10 people"
                      className="w-full bg-white border-zinc-300 h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[14px] font-medium text-[#1d1d1f] mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 000-0000"
                    className="w-full bg-white border-zinc-300 h-12"
                  />
                </div>

                <div>
                  <label className="block text-[14px] font-medium text-[#1d1d1f] mb-2">
                    Tell us about your needs
                  </label>
                  <Textarea
                    name="description"
                    value={contactForm.description}
                    onChange={handleInputChange}
                    placeholder="What are your team's requirements? Any specific compliance or deployment needs?"
                    rows={5}
                    className="w-full bg-white border-zinc-300"
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#0071e3] via-[#00d084] to-[#0071e3] bg-[length:200%_auto] text-white rounded-full text-[16px] font-semibold tracking-wide hover:animate-gradient-x transition-all transform hover:scale-[1.02] shadow-xl hover:shadow-2xl liquid-shimmer overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 relative z-10" />
                        <span className="relative z-10">Send Message</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Enterprise;
